/**
 * SplashScreen.jsx
 * App splash/loading screen with StrongFit branding
 */
import React, { useEffect, useState, useRef } from 'react';
import WinterHat from '../WinterHat';
import { useTranslation } from 'react-i18next';

export const SplashScreen = ({ 
  onComplete, 
  winterHatEnabled = false,
  splashVideoUrl = '/assets/intro-video.mp4',
  splashLogoSize = 280,
  splashFrameSize = 110,
  logoUrl = '/assets/invictafit-logo.png',
  appName = '',
  appSlogan = 'Dein Personal Training Studio'
}) => {
  const [showVideo, setShowVideo] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    // Fallback timeout if video doesn't load or play
    const timer = setTimeout(() => {
      onComplete && onComplete();
    }, 8000); // 8 Sekunden Fallback
    return () => clearTimeout(timer);
  }, [onComplete, splashVideoUrl]);

  const handleVideoEnd = () => {
    onComplete && onComplete();
  };

  const handleVideoError = () => {
    setVideoError(true);
    // Show logo for 2 seconds then continue
    setTimeout(() => {
      onComplete && onComplete();
    }, 2000);
  };

  return (
    <div className="h-full flex flex-col items-center justify-center p-4 relative z-10">
      {/* Video or Logo - beide mit dem gleichen Rahmen-Stil */}
      {showVideo && !videoError ? (
        <>
          {/* Video im goldenen Rahmen - Grössen aus Einstellungen */}
          <div 
            className="relative rounded-2xl flex items-center justify-center mb-6 overflow-visible"
            style={{
              width: `${splashFrameSize}px`,
              height: `${splashFrameSize}px`,
              backgroundColor: '#000000',
              boxShadow: '0 0 30px rgba(201, 162, 39, 0.5), 0 0 60px rgba(201, 162, 39, 0.3)',
              border: '2px solid rgba(201, 162, 39, 0.4)'
            }}
          >
            {/* Winter Hat */}
            {winterHatEnabled && <WinterHat size="large" enabled={winterHatEnabled} />}
            
            {/* Inner glow */}
            <div 
              className="absolute inset-0 rounded-2xl"
              style={{
                background: 'radial-gradient(circle at center, rgba(201, 162, 39, 0.2) 0%, transparent 70%)'
              }}
            />
            <video
              ref={videoRef}
              src={splashVideoUrl}
              autoPlay
              muted
              playsInline
              onEnded={handleVideoEnd}
              onError={handleVideoError}
              className="object-contain relative z-10"
              style={{ 
                width: `${splashLogoSize}px`,
                height: `${splashLogoSize}px`,
                minWidth: `${splashLogoSize}px`,
                minHeight: `${splashLogoSize}px`,
                filter: 'drop-shadow(0 0 10px rgba(201, 162, 39, 0.6))'
              }}
            />
          </div>
          
          <h1 className="text-white text-2xl font-bold mb-2 mt-20">StrongFit</h1>
          <p className="text-zinc-500 text-sm mb-8">Dein Personal Training Studio</p>
          
          {/* Loading Animation */}
          <div className="flex gap-1.5">
            <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#C9A227', animationDelay: '0ms' }} />
            <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#C9A227', animationDelay: '150ms' }} />
            <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#C9A227', animationDelay: '300ms' }} />
          </div>
        </>
      ) : (
        <>
          {/* StrongFit Logo with Glow Effect */}
          <div 
            className="relative w-28 h-28 rounded-2xl flex items-center justify-center mb-6"
            style={{
              backgroundColor: '#000000',
              boxShadow: '0 0 30px rgba(201, 162, 39, 0.5), 0 0 60px rgba(201, 162, 39, 0.3)',
              border: '2px solid rgba(201, 162, 39, 0.4)'
            }}
          >
            {/* Winter Hat */}
            {winterHatEnabled && <WinterHat size="large" enabled={winterHatEnabled} />}
            
            {/* Inner glow */}
            <div 
              className="absolute inset-0 rounded-2xl animate-pulse"
              style={{
                background: 'radial-gradient(circle at center, rgba(201, 162, 39, 0.2) 0%, transparent 70%)'
              }}
            />
            <img 
              src={logoUrl} 
              alt={appName || 'Logo'}
              className="w-20 h-20 object-contain relative z-10"
              style={{
                filter: 'drop-shadow(0 0 10px rgba(201, 162, 39, 0.6))'
              }}
            />
          </div>
          
          <h1 className="text-white text-2xl font-bold mb-2">{appName}</h1>
          <p className="text-zinc-500 text-sm mb-8">{appSlogan}</p>
          
          {/* Loading Animation */}
          <div className="flex gap-1.5">
            <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#C9A227', animationDelay: '0ms' }} />
            <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#C9A227', animationDelay: '150ms' }} />
            <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: '#C9A227', animationDelay: '300ms' }} />
          </div>
        </>
      )}
    </div>
  );
};

export default SplashScreen;
